"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Link from "next/link";
import {
  portalRegister,
  portalLogin,
  portalVerifyEmail,
  portalResendVerification,
  portalCreateCompany,
  portalGetCompanies,
  portalCheckSubdomain,
  isPendingVerification,
  type PortalUser,
  type PortalCompany,
  type PortalApiError,
  type PortalRegisterResponse,
} from "@/lib/portal-api";

/* ═══════════════════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════════════════ */

const BLOCKED_SUBDOMAINS = [
  "portal","admin","www","api","app","mail","ftp","support","test","demo",
  "payroll","workspace","sekanipay","hrms","billing","docs","help","status",
];

const MODULES = [
  { id: "Organisation",        name: "Organisation",      desc: "Organization structure, policies and company records.",              price: 0,    group: "included", required: true  as const },
  { id: "People",              name: "People",            desc: "Employee profiles, directories and self-service.",                    price: 0,    group: "included", required: true  as const },
  { id: "Reports",             name: "Reports",           desc: "Generate HR reports and analytics.",                                   price: 0,    group: "included", required: true  as const },
  { id: "Approvals",           name: "Approvals",         desc: "Approval workflows for HR processes.",                                 price: 0,    group: "included", required: true  as const },
  { id: "Document",            name: "Document",          desc: "Secure employee and HR document storage.",                             price: 50,   group: "grow",     required: false as const },
  { id: "Asset",               name: "Asset",             desc: "Track equipment and assets assigned to employees.",                    price: 30,   group: "grow",     required: false as const },
  { id: "Helpdesk",            name: "Helpdesk",          desc: "Employee questions, requests and HR case management.",                 price: 50,   group: "grow",     required: false as const },
  { id: "Recruitment",         name: "Recruitment",       desc: "Vacancies, applicants and candidate pipelines.",                       price: 10,   group: "grow",     required: false as const },
  { id: "Analytics",           name: "Analytics",         desc: "Advanced analytics for HR data.",                                      price: 50,   group: "grow",     required: false as const },
  { id: "Performance",         name: "Performance",       desc: "Goals, reviews and performance cycles.",                               price: 50,   group: "grow",     required: false as const },
  { id: "Leave",               name: "Leave",             desc: "Leave requests, approvals, balances and policies.",                    price: 60,   group: "popular",  required: false as const },
  { id: "Payroll",             name: "Payroll",           desc: "Salary runs, payslips and statutory payroll workflows.",                price: 70,   group: "popular",  required: false as const },
  { id: "Attendance",          name: "Attendance",        desc: "Clock-ins, shifts and attendance logs.",                               price: 70,   group: "popular",  required: false as const },
] as const;

type ModuleId = (typeof MODULES)[number]["id"];

const MODULE_GROUPS = [
  { id: "included", title: "Included with every workspace",  desc: "Core HR foundation" },
  { id: "popular",  title: "Popular for Kenyan teams",       desc: "Payroll, attendance and leave" },
  { id: "finance",  title: "Finance operations",             desc: "Expense workflows" },
  { id: "grow",     title: "Grow your workspace",            desc: "Talent and operations modules" },
];

const PROVISION_TASKS = [
  "Creating organization",
  "Creating primary company",
  "Provisioning SekaniPay tenant",
  "Securing workspace subdomain",
  "Activating selected modules",
  "Creating workspace owner access",
  "Preparing team invitations",
  "Finalizing workspace",
];

const COUNTRIES = ["Kenya","Uganda","Tanzania"];
const INDUSTRIES = ["Technology","Financial Services","Healthcare","Manufacturing","Retail","Education","Hospitality","Professional Services","Other"];
const SIZES = ["1-10","11-50","51-200","201-500","500+"];

/* ═══════════════════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════════════════ */

type View = "register" | "signin" | "verify" | "onboarding" | "provisioning" | "success";
type DrawerType = "business" | "workspace" | "modules" | "invites" | "review" | null;

interface BusinessDetails { company: string; country: string; industry: string; size: string; }
interface WorkspaceDetails { subdomain: string; status: "idle" | "checking" | "available" | "unavailable" | "invalid"; }
interface Invite { email: string; role: string; }

/* ═══════════════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════════════ */

function getModule(id: string) { return MODULES.find(m => m.id === id); }
function initials(name: string) { return name.split(/\s+/).slice(0, 2).map(x => x[0] || "").join("").toUpperCase(); }
function formatNumber(n: number) { return Number(n).toLocaleString("en-KE"); }
function delay(ms: number) { return new Promise(r => setTimeout(r, ms)); }

function moduleTotal(selected: Set<string>) {
  return Array.from(selected).reduce((sum, id) => sum + (getModule(id)?.price ?? 0), 0);
}

function selectedDependents(_id: string, _selected: Set<string>): string[] {
  return [];
}

function validInvites(invites: Invite[]) { return invites.filter(i => /^\S+@\S+\.\S+$/.test(i.email.trim())); }
function businessComplete(b: BusinessDetails) { return !!(b.company && b.country); }
function workspaceComplete(w: WorkspaceDetails) { return w.status === "available"; }

function extractApiError(err: unknown): string {
  const e = err as PortalApiError;
  if (e.errors) {
    const first = Object.values(e.errors)[0];
    return first?.[0] ?? e.message;
  }
  return e.message || "Something went wrong. Please try again.";
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main Page Component
   ═══════════════════════════════════════════════════════════════════════════ */

export default function WorkspacePage() {
  // ── View state ────────────────────────────────────────────
  const [view, setView] = useState<View>("register");
  const [drawer, setDrawer] = useState<DrawerType>(null);

  // ── Auth state ────────────────────────────────────────────
  const [token, setToken] = useState("");
  const [account, setAccount] = useState({ name: "", email: "" });
  const [pendingEmail, setPendingEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // ── Onboarding state ─────────────────────────────────────
  const [business, setBusiness] = useState<BusinessDetails>({ company: "", country: "", industry: "", size: "" });
  const [workspace, setWorkspace] = useState<WorkspaceDetails>({ subdomain: "", status: "idle" });
  const [selectedModules, setSelectedModules] = useState<Set<string>>(
    new Set(MODULES.filter(m => m.required).map(m => m.id))
  );
  const [modulesReviewed, setModulesReviewed] = useState(false);
  const [invites, setInvites] = useState<Invite[]>([{ email: "", role: "HR Admin" }]);
  const [existingCompanies, setExistingCompanies] = useState<PortalCompany[]>([]);

  // ── Provisioning state ────────────────────────────────────
  const [createdCompany, setCreatedCompany] = useState<PortalCompany | null>(null);
  const [provisionStep, setProvisionStep] = useState(-1);
  const [provisionComplete, setProvisionComplete] = useState<boolean[]>(new Array(PROVISION_TASKS.length).fill(false));

  // ── UI state ──────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ title: string; message: string } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const domainTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const domainToken = useRef(0);

  // ── Drawer form refs ──────────────────────────────────────
  const [draftBusiness, setDraftBusiness] = useState<BusinessDetails>({ company: "", country: "", industry: "", size: "" });
  const [draftSubdomain, setDraftSubdomain] = useState("");
  const [launchConfirmed, setLaunchConfirmed] = useState(false);

  // ── Derived state ─────────────────────────────────────────
  const essentialsComplete = businessComplete(business) && workspaceComplete(workspace) && modulesReviewed;

  // ── Toast helper ──────────────────────────────────────────
  const showToast = useCallback((title: string, message: string) => {
    clearTimeout(toastTimer.current);
    setToast({ title, message });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Auth handlers ─────────────────────────────────────────

  async function handleRegister(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const form = new FormData(e.currentTarget);
    const name = (form.get("name") as string).trim();
    const email = (form.get("email") as string).trim();
    const password = form.get("password") as string;
    const terms = form.get("terms") === "on";

    if (!name || !email || password.length < 8 || !terms) {
      setError("Enter your name, a valid work email, an 8+ character password and accept the terms.");
      return;
    }

    setLoading(true);
    try {
      const res = await portalRegister({ name, email, password, password_confirmation: password });
      if (isPendingVerification(res)) {
        setAccount({ name, email });
        setPendingEmail(res.email);
        setView("verify");
      } else {
        setToken(res.token);
        setAccount({ name: res.user.name, email: res.user.email });
        await checkExistingCompanies(res.token);
        setView("onboarding");
      }
    } catch (err) {
      const msg = extractApiError(err);
      if (msg.toLowerCase().includes("already been taken") || msg.toLowerCase().includes("already exists")) {
        setError("This email already has an account. Sign in instead to continue.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSignIn(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const form = new FormData(e.currentTarget);
    const email = (form.get("email") as string).trim();
    const password = form.get("password") as string;

    if (!email || !password) {
      setError("Enter your email and password.");
      return;
    }

    setLoading(true);
    try {
      const res = await portalLogin({ email, password });
      if (isPendingVerification(res as unknown as PortalRegisterResponse)) {
        const pending = res as unknown as { requires_verification: true; email: string };
        setAccount({ name: "", email: pending.email });
        setPendingEmail(pending.email);
        setView("verify");
      } else {
        setToken(res.token);
        setAccount({ name: res.user.name, email: res.user.email });
        await checkExistingCompanies(res.token);
        setView("onboarding");
      }
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }

  // ── Verification handlers ─────────────────────────────────

  async function handleVerify(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    const code = verifyCode.replace(/\s/g, "");
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setLoading(true);
    try {
      const res = await portalVerifyEmail({ email: pendingEmail, code });
      setToken(res.token);
      setAccount({ name: res.user.name, email: res.user.email });
      await checkExistingCompanies(res.token);
      setView("onboarding");
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleResendCode() {
    if (resendCooldown > 0) return;
    try {
      await portalResendVerification({ email: pendingEmail });
      showToast("Code sent", "A new verification code has been sent to your email.");
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch {
      showToast("Error", "Could not resend code. Please try again.");
    }
  }

  async function checkExistingCompanies(authToken: string) {
    try {
      const res = await portalGetCompanies(authToken);
      setExistingCompanies(res.data ?? []);
    } catch {
      // Not critical - continue with empty list
    }
  }

  // ── Subdomain check ───────────────────────────────────────

  function handleSubdomainInput(raw: string) {
    const cleaned = raw.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setDraftSubdomain(cleaned);

    clearTimeout(domainTimer.current);

    if (!cleaned) {
      setWorkspace({ subdomain: cleaned, status: "idle" });
      return;
    }

    if (cleaned.length < 3 || cleaned.length > 32 || cleaned.startsWith("-") || cleaned.endsWith("-")) {
      setWorkspace({ subdomain: cleaned, status: "invalid" });
      return;
    }

    if (BLOCKED_SUBDOMAINS.includes(cleaned)) {
      setWorkspace({ subdomain: cleaned, status: "unavailable" });
      return;
    }

    setWorkspace({ subdomain: cleaned, status: "checking" });
    const currentToken = ++domainToken.current;

    domainTimer.current = setTimeout(async () => {
      if (currentToken !== domainToken.current) return;
      try {
        const res = await portalCheckSubdomain(cleaned);
        if (currentToken !== domainToken.current) return;
        setWorkspace({ subdomain: cleaned, status: res.available ? "available" : "unavailable" });
      } catch {
        if (currentToken !== domainToken.current) return;
        setWorkspace({ subdomain: cleaned, status: "unavailable" });
      }
    }, 650);
  }

  // ── Module toggle ─────────────────────────────────────────

  function toggleModule(id: string) {
    const mod = getModule(id);
    if (!mod) return;
    if (mod.required) { showToast("Included by default", `${mod.name} is part of every workspace.`); return; }

    setSelectedModules(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  // ── Drawer save handlers ──────────────────────────────────

  function saveBusiness() {
    if (!draftBusiness.company || !draftBusiness.country) {
      showToast("Missing information", "Primary company and country are required.");
      return;
    }
    setBusiness({ ...draftBusiness });
    setDrawer(null);
    showToast("Business details saved", `${draftBusiness.company} is ready for setup.`);
  }

  function saveWorkspace() {
    if (workspace.status !== "available") {
      showToast("Choose an available address", "The subdomain must be valid and available before you continue.");
      return;
    }
    setDrawer(null);
    showToast("Workspace address saved", `${workspace.subdomain}.sekanipay.com is ready for provisioning.`);
  }

  function saveModules() {
    setModulesReviewed(true);
    setDrawer(null);
    showToast("Modules saved", `${selectedModules.size} modules selected.`);
  }

  function saveInvites() {
    setDrawer(null);
    const c = validInvites(invites).length;
    showToast(c ? "Invitations saved" : "No invitations added",
      c ? `${c} invitation${c === 1 ? "" : "s"} will be prepared after setup.` : "You can invite team members later.");
  }

  // ── Open drawer helpers ───────────────────────────────────

  function openDrawer(type: DrawerType) {
    if (type === "business") setDraftBusiness({ ...business });
    if (type === "workspace") setDraftSubdomain(workspace.subdomain);
    if (type === "review") setLaunchConfirmed(false);
    setDrawer(type);
  }

  // ── Create workspace (provision) ──────────────────────────

  async function createWorkspace() {
    if (!launchConfirmed) {
      showToast("Confirmation required", "Confirm your workspace details before creating the tenant.");
      return;
    }

    setDrawer(null);
    setView("provisioning");
    setProvisionStep(-1);
    setProvisionComplete(new Array(PROVISION_TASKS.length).fill(false));

    // Start the animated provisioning display
    const progressPromise = (async () => {
      for (let i = 0; i < PROVISION_TASKS.length; i++) {
        setProvisionStep(i);
        await delay(520);
        setProvisionComplete(prev => { const next = [...prev]; next[i] = true; return next; });
      }
    })();

    // Simultaneously call the API to create the company
    try {
      const moduleNames = Array.from(selectedModules).map(id => getModule(id)?.name).filter(Boolean) as string[];
      const res = await portalCreateCompany(token, {
        company_name: business.company,
        sub_domain: workspace.subdomain,
        country: business.country,
        modules: moduleNames,
      });
      setCreatedCompany(res.data);
    } catch (err) {
      const msg = extractApiError(err);
      // Let the animation finish before showing error
      await progressPromise;
      setError(msg);
      showToast("Provisioning failed", msg);
      setView("onboarding");
      // If subdomain was taken, reset workspace status
      if (msg.toLowerCase().includes("sub_domain") || msg.toLowerCase().includes("subdomain") || msg.toLowerCase().includes("taken")) {
        setWorkspace(prev => ({ ...prev, status: "unavailable" }));
      }
      return;
    }

    // Wait for animation to complete
    await progressPromise;
    await delay(450);
    setView("success");
  }

  // ── Open tenant redirect ──────────────────────────────────

  function openTenant() {
    const url = `https://${workspace.subdomain}.sekanipay.com`;
    window.open(url, "_blank", "noopener");
  }

  /* ═════════════════════════════════════════════════════════════════════════
     Render
     ═════════════════════════════════════════════════════════════════════════ */

  return (
    <div className="min-h-screen bg-[#F3F6FA] text-[#101828]">
      {/* ── Toast ──────────────────────────────────────────────── */}
      {toast && (
        <div className="fixed right-5 top-5 z-[100] max-w-sm rounded-sm border border-slate-200 bg-white p-4 shadow-lg animate-fade-up">
          <div className="flex gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sekani-50 text-sekani-700 text-sm">✓</div>
            <div>
              <p className="text-sm font-semibold">{toast.title}</p>
              <p className="mt-1 text-sm leading-5 text-slate-500">{toast.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
         REGISTER VIEW
         ═══════════════════════════════════════════════════════════════════ */}
      {view === "register" && (
        <section className="min-h-screen bg-[#F3F6FA] animate-fade-up">
          <header className="border-b border-[#DCE3EA] bg-[#F3F6FA]">
            <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-8">
              <Link href="/" className="text-left">
                <div className="wordmark text-slate-950">SekaniPay</div>
                <div className="mono mt-1 text-[9px] uppercase tracking-[.1em] text-slate-400">Workspace account</div>
              </Link>
              <p className="text-[12px] text-slate-500">
                Already registered?{" "}
                <button onClick={() => { setView("signin"); setError(""); }} className="quiet-link font-medium text-slate-900 underline decoration-[#AAA89F]">
                  Sign in
                </button>
              </p>
            </div>
          </header>

          <main className="mx-auto grid min-h-[calc(100vh-73px)] max-w-6xl gap-10 px-5 py-10 sm:px-8 lg:grid-cols-[.9fr_1.1fr] lg:items-center lg:py-16">
            <div className="hidden lg:block">
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Account / Workspace owner</p>
              <h1 className="mt-4 max-w-md text-4xl font-bold tracking-tight">Create the account that owns your workspace.</h1>
              <p className="mt-4 max-w-lg leading-7 text-slate-600">Registration creates you as the workspace owner. The organization, company, URL and modules are configured immediately after.</p>
              <div className="mt-8 space-y-4 text-sm text-slate-600">
                <div className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-sekani-50 text-xs text-sekani-700">1</span><span>Create your user account</span></div>
                <div className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-sekani-50 text-xs text-sekani-700">2</span><span>Set up your organization and first company</span></div>
                <div className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-sekani-50 text-xs text-sekani-700">3</span><span>Choose your SekaniPay modules</span></div>
              </div>
            </div>

            <div className="mx-auto w-full max-w-lg square-card border border-[#DCE3EA] bg-white p-6 sm:p-8">
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">New workspace owner</p>
              <h2 className="mt-4 text-2xl font-bold tracking-tight">Start your SekaniPay workspace</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">Your free calculator access does not change. This account is for the business workspace.</p>

              {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}

              <form className="mt-7 space-y-5" onSubmit={handleRegister}>
                <div>
                  <label htmlFor="reg-name" className="mb-2 block text-sm font-medium">Full name</label>
                  <input id="reg-name" name="name" autoComplete="name" placeholder="Jasiri" className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="reg-email" className="mb-2 block text-sm font-medium">Work email</label>
                  <input id="reg-email" name="email" type="email" autoComplete="email" placeholder="jasiri@sekanipay.com" className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="reg-password" className="mb-2 block text-sm font-medium">Password</label>
                  <input id="reg-password" name="password" type="password" autoComplete="new-password" placeholder="At least 8 characters" className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none" />
                </div>
                <label className="flex cursor-pointer items-start gap-3">
                  <input name="terms" type="checkbox" className="mt-1 h-4 w-4 border-slate-300 accent-sekani-700" />
                  <span className="text-xs leading-5 text-slate-500">I agree to the <Link href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</Link> and <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</Link>.</span>
                </label>
                <button type="submit" disabled={loading} className="ink-button w-full bg-[#0F1B2D] px-5 py-3.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                  {loading ? "Creating account…" : "Create account →"}
                </button>
              </form>
            </div>
          </main>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
         EMAIL VERIFICATION VIEW
         ═══════════════════════════════════════════════════════════════════ */}
      {view === "verify" && (
        <section className="min-h-screen bg-[#F3F6FA] animate-fade-up">
          <header className="border-b border-[#DCE3EA] bg-[#F3F6FA]">
            <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-8">
              <Link href="/" className="text-left">
                <div className="wordmark text-slate-950">SekaniPay</div>
                <div className="mono mt-1 text-[9px] uppercase tracking-[.1em] text-slate-400">Email verification</div>
              </Link>
              <p className="text-[12px] text-slate-500">
                Wrong email?{" "}
                <button onClick={() => { setView("register"); setError(""); setVerifyCode(""); }} className="quiet-link font-medium text-slate-900 underline decoration-[#AAA89F]">
                  Start over
                </button>
              </p>
            </div>
          </header>

          <main className="mx-auto grid min-h-[calc(100vh-73px)] max-w-6xl gap-10 px-5 py-10 sm:px-8 lg:grid-cols-[.9fr_1.1fr] lg:items-center lg:py-16">
            <div className="hidden lg:block">
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Verification</p>
              <h1 className="mt-4 max-w-md text-4xl font-bold tracking-tight">Check your inbox for a 6-digit code.</h1>
              <p className="mt-4 max-w-lg leading-7 text-slate-600">We sent a verification code to <strong className="font-semibold text-slate-900">{pendingEmail}</strong>. Enter it below to verify your email and continue to workspace setup.</p>
              <div className="mt-8 space-y-4 text-sm text-slate-600">
                <div className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#ECFDF5] text-xs text-[#0F7B4D]">✓</span><span>Account created</span></div>
                <div className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-sekani-50 text-xs text-sekani-700">2</span><span>Verify your email</span></div>
                <div className="flex gap-3"><span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs text-slate-400">3</span><span className="text-slate-400">Set up your workspace</span></div>
              </div>
            </div>

            <div className="mx-auto w-full max-w-lg square-card border border-[#DCE3EA] bg-white p-6 sm:p-8">
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Email verification</p>
              <h2 className="mt-4 text-2xl font-bold tracking-tight">Enter verification code</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                We sent a 6-digit code to <strong className="font-medium text-slate-700">{pendingEmail}</strong>. The code expires in 15 minutes.
              </p>

              {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}

              <form className="mt-7 space-y-5" onSubmit={handleVerify}>
                <div>
                  <label htmlFor="verify-code" className="mb-2 block text-sm font-medium">Verification code</label>
                  <input
                    id="verify-code"
                    value={verifyCode}
                    onChange={e => {
                      const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                      setVerifyCode(v);
                    }}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    className="mono w-full border border-slate-200 bg-white px-4 py-3 text-center text-2xl tracking-[.3em] square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none"
                  />
                </div>
                <button type="submit" disabled={loading || verifyCode.length !== 6} className="ink-button w-full bg-[#0F1B2D] px-5 py-3.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                  {loading ? "Verifying…" : "Verify email →"}
                </button>
              </form>

              <div className="mt-6 border-t border-[#DCE3EA] pt-5 text-center">
                <p className="text-[13px] text-slate-500">
                  Didn&apos;t receive the code?{" "}
                  <button
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0}
                    className="quiet-link font-medium text-[#2348C7] underline decoration-[#2348C7] disabled:text-slate-400 disabled:no-underline"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                  </button>
                </p>
              </div>
            </div>
          </main>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
         SIGN IN VIEW
         ═══════════════════════════════════════════════════════════════════ */}
      {view === "signin" && (
        <section className="min-h-screen bg-[#F3F6FA] animate-fade-up">
          <header className="border-b border-[#DCE3EA] bg-[#F3F6FA]">
            <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-8">
              <Link href="/" className="text-left">
                <div className="wordmark text-slate-950">SekaniPay</div>
                <div className="mono mt-1 text-[9px] uppercase tracking-[.1em] text-slate-400">Workspace sign in</div>
              </Link>
              <p className="text-[12px] text-slate-500">
                Need an account?{" "}
                <button onClick={() => { setView("register"); setError(""); }} className="quiet-link font-medium text-slate-900 underline decoration-[#AAA89F]">
                  Register
                </button>
              </p>
            </div>
          </header>

          <main className="mx-auto grid min-h-[calc(100vh-73px)] max-w-6xl gap-10 px-5 py-10 sm:px-8 lg:grid-cols-[.9fr_1.1fr] lg:items-center lg:py-16">
            <div className="hidden lg:block">
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Workspace owner</p>
              <h1 className="mt-4 max-w-md text-4xl font-bold tracking-tight">Sign in to manage your workspaces.</h1>
              <p className="mt-4 max-w-lg leading-7 text-slate-600">Access your existing company workspaces or create a new one for another organization.</p>
            </div>

            <div className="mx-auto w-full max-w-lg square-card border border-[#DCE3EA] bg-white p-6 sm:p-8">
              <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Workspace access</p>
              <h2 className="mt-4 text-2xl font-bold tracking-tight">Sign in to SekaniPay</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">Use the email and password you registered with.</p>

              {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}

              <form className="mt-7 space-y-5" onSubmit={handleSignIn}>
                <div>
                  <label htmlFor="signin-email" className="mb-2 block text-sm font-medium">Email</label>
                  <input id="signin-email" name="email" type="email" autoComplete="email" placeholder="jasiri@sekanipay.com" className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="signin-password" className="mb-2 block text-sm font-medium">Password</label>
                  <input id="signin-password" name="password" type="password" autoComplete="current-password" placeholder="Your password" className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none" />
                </div>
                <button type="submit" disabled={loading} className="ink-button w-full bg-[#0F1B2D] px-5 py-3.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                  {loading ? "Signing in…" : "Sign in →"}
                </button>
              </form>
            </div>
          </main>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
         ONBOARDING HUB VIEW
         ═══════════════════════════════════════════════════════════════════ */}
      {view === "onboarding" && (
        <section className="min-h-screen bg-[#F3F6FA] animate-fade-up">
          <header className="border-b border-[#DCE3EA] bg-[#F3F6FA]">
            <div className="mx-auto grid h-[72px] max-w-7xl grid-cols-[1fr_auto] items-center px-5 sm:px-8 lg:grid-cols-[220px_1fr_auto]">
              <div>
                <p className="wordmark text-slate-950">SekaniPay</p>
                <p className="mono mt-1 text-[9px] uppercase tracking-[.1em] text-slate-400">Tenant setup</p>
              </div>
              <div className="hidden border-l border-[#DCE3EA] pl-6 text-[11px] text-slate-500 lg:block">Organization → address → modules → launch</div>
              <div className="flex items-center gap-4">
                <span className="mono hidden text-[9px] uppercase tracking-[.1em] text-slate-400 sm:block">Autosave on</span>
                <div className="mono flex h-8 w-8 items-center justify-center border border-[#CBD5E1] bg-transparent text-[10px] text-slate-700">
                  {initials(account.name) || "SP"}
                </div>
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-7xl px-5 py-9 sm:px-8 lg:py-14">
            {/* Existing companies notice */}
            {existingCompanies.length > 0 && (
              <div className="mb-8 square-card border border-[#79AEFF] bg-sekani-50 p-5">
                <p className="text-sm font-medium text-sekani-800">You already have {existingCompanies.length} workspace{existingCompanies.length > 1 ? "s" : ""}</p>
                <div className="mt-2 space-y-1">
                  {existingCompanies.map(c => (
                    <p key={c.id} className="text-[13px] text-sekani-700">
                      {c.company_name} — <a href={`https://${c.sub_domain}.sekanipay.com`} target="_blank" rel="noopener noreferrer" className="underline">{c.sub_domain}.sekanipay.com</a>
                    </p>
                  ))}
                </div>
                <p className="mt-2 text-[12px] text-slate-500">You can create another company below, or access an existing workspace above.</p>
              </div>
            )}

            {/* Welcome heading */}
            <div className="mb-12 grid max-w-4xl gap-4 sm:grid-cols-[110px_1fr]">
              <div className="mono pt-1 text-[9px] uppercase leading-5 tracking-[.1em] text-slate-400">Setup file<br />Tenant {String(existingCompanies.length + 1).padStart(3, "0")}</div>
              <div>
                <p className="mono text-[10px] uppercase tracking-[.13em] text-[#2348C7]">Company workspace</p>
                <h1 className="mt-3 max-w-2xl text-4xl leading-[1.04] tracking-[-.025em] sm:text-5xl">
                  Welcome, {account.name.split(" ")[0]}. Let&apos;s set up SekaniPay for your team
                </h1>
                <p className="mt-4 max-w-xl text-[15px] leading-7 text-slate-600">Add the organization, reserve its SekaniPay address and decide which modules should be active at launch.</p>
              </div>
            </div>

            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_330px]">
              <div>
                {/* Progress register */}
                {(() => {
                  const done = [businessComplete(business), workspaceComplete(workspace), modulesReviewed].filter(Boolean).length;
                  const pct = Math.round(done / 3 * 100);
                  return (
                    <div className="mb-3 grid grid-cols-[88px_1fr_auto] items-end gap-4 border-y border-[#CBD5E1] py-4">
                      <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Progress</span>
                      <div>
                        <p className="text-[13px] text-slate-700">{done} of 3 essentials completed</p>
                        <div className="mt-3 h-[3px] bg-[#DCE3EA]"><div className="h-full bg-[#2348C7] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
                      </div>
                      <span className="mono text-[11px] text-[#2348C7]">{pct}%</span>
                    </div>
                  );
                })()}

                {/* Setup register */}
                <div className="border-b border-[#CBD5E1]">
                  {/* 01 Business details */}
                  <button onClick={() => openDrawer("business")} className="group grid w-full grid-cols-[56px_1fr_auto] gap-4 border-t border-[#DCE3EA] py-6 text-left transition hover:bg-white/40 sm:grid-cols-[76px_1fr_auto]">
                    <div className="mono text-[10px] text-slate-400">01</div>
                    <div>
                      <p className="text-[15px] font-medium">Business details</p>
                      <p className="mt-1 text-[13px] leading-6 text-slate-500">
                        {businessComplete(business) ? `${business.company} · ${business.country}` : "Primary company and country."}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`mono text-[9px] uppercase tracking-[.1em] ${businessComplete(business) ? "text-[#0F7B4D]" : "text-slate-400"}`}>
                        {businessComplete(business) ? "Complete" : "Required"}
                      </span>
                      <span className="text-slate-400 transition group-hover:translate-x-1 group-hover:text-[#2348C7]">→</span>
                    </div>
                  </button>

                  {/* 02 Workspace address */}
                  <button onClick={() => openDrawer("workspace")} className="group grid w-full grid-cols-[56px_1fr_auto] gap-4 border-t border-[#DCE3EA] py-6 text-left transition hover:bg-white/40 sm:grid-cols-[76px_1fr_auto]">
                    <div className="mono text-[10px] text-slate-400">02</div>
                    <div>
                      <p className="text-[15px] font-medium">Workspace address</p>
                      <p className="mt-1 text-[13px] leading-6 text-slate-500">
                        {workspaceComplete(workspace) ? `${workspace.subdomain}.sekanipay.com` : "Reserve the tenant URL used by your team."}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`mono text-[9px] uppercase tracking-[.1em] ${workspaceComplete(workspace) ? "text-[#0F7B4D]" : "text-slate-400"}`}>
                        {workspaceComplete(workspace) ? "Complete" : "Required"}
                      </span>
                      <span className="text-slate-400 transition group-hover:translate-x-1 group-hover:text-[#2348C7]">→</span>
                    </div>
                  </button>

                  {/* 03 Modules */}
                  <button onClick={() => openDrawer("modules")} className="group grid w-full grid-cols-[56px_1fr_auto] gap-4 border-t border-[#DCE3EA] py-6 text-left transition hover:bg-white/40 sm:grid-cols-[76px_1fr_auto]">
                    <div className="mono text-[10px] text-slate-400">03</div>
                    <div>
                      <p className="text-[15px] font-medium">Workspace modules</p>
                      <p className="mt-1 text-[13px] leading-6 text-slate-500">
                        {modulesReviewed ? `${selectedModules.size} modules · KSh ${formatNumber(moduleTotal(selectedModules))}/month` : "Core HR and Employees are already part of the base."}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`mono text-[9px] uppercase tracking-[.1em] ${modulesReviewed ? "text-[#0F7B4D]" : "text-slate-400"}`}>
                        {modulesReviewed ? "Complete" : "Required"}
                      </span>
                      <span className="text-slate-400 transition group-hover:translate-x-1 group-hover:text-[#2348C7]">→</span>
                    </div>
                  </button>

                  {/* 04 Administrators (optional) — commented out */}
                  {false && (
                  <button onClick={() => openDrawer("invites")} className="group grid w-full grid-cols-[56px_1fr_auto] gap-4 border-t border-[#DCE3EA] py-6 text-left transition hover:bg-white/40 sm:grid-cols-[76px_1fr_auto]">
                    <div className="mono text-[10px] text-slate-400">04</div>
                    <div>
                      <p className="text-[15px] font-medium">Administrators</p>
                      <p className="mt-1 text-[13px] leading-6 text-slate-500">
                        {validInvites(invites).length > 0
                          ? `${validInvites(invites).length} administrator${validInvites(invites).length === 1 ? "" : "s"} will be invited`
                          : "Add colleagues who will help run HR and payroll."}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Optional</span>
                      <span className="text-slate-400 transition group-hover:translate-x-1 group-hover:text-[#2348C7]">→</span>
                    </div>
                  </button>
                  )}

                  {/* 05 Review & launch */}
                  <button
                    onClick={() => essentialsComplete && openDrawer("review")}
                    disabled={!essentialsComplete}
                    className={`group grid w-full grid-cols-[56px_1fr_auto] gap-4 border-t border-[#DCE3EA] py-6 text-left transition sm:grid-cols-[76px_1fr_auto] ${
                      essentialsComplete ? "hover:bg-white/60 bg-white/35" : "cursor-not-allowed opacity-45"
                    }`}
                  >
                    <div className={`mono text-[10px] ${essentialsComplete ? "text-[#2348C7]" : "text-slate-400"}`}>05</div>
                    <div>
                      <p className="text-[15px] font-medium">Review & launch</p>
                      <p className="mt-1 text-[13px] leading-6 text-slate-500">
                        {essentialsComplete ? "Everything essential is ready. Review the setup before launch." : "Complete the three required records first."}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`mono text-[9px] uppercase tracking-[.1em] ${essentialsComplete ? "text-[#2348C7]" : "text-slate-400"}`}>
                        {essentialsComplete ? "Ready" : "Locked"}
                      </span>
                      <span className="text-slate-400">→</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Tenant docket sidebar */}
              <aside className="lg:sticky lg:top-28 lg:self-start">
                <div className="square-card border border-[#CBD5E1] bg-white">
                  <div className="border-b border-[#DCE3EA] bg-[#101828] p-5 text-white" style={{ borderRadius: "4px 4px 0 0" }}>
                    <p className="mono text-[9px] uppercase tracking-[.11em] text-white/45">Tenant docket</p>
                    <h2 className="mt-3 text-[18px] font-medium">{business.company || "Your company"}</h2>
                    <p className="mt-1 text-[12px] text-white/45">{business.company || "Primary company not added"}</p>
                  </div>
                  <div className="p-5">
                    <div className="border-b border-[#E0DFD9] pb-4">
                      <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Address</p>
                      <p className="mono mt-2 truncate text-[11px] text-slate-700">{workspace.subdomain ? `${workspace.subdomain}.sekanipay.com` : "—.sekanipay.com"}</p>
                    </div>
                    <div className="grid grid-cols-2 border-b border-[#E0DFD9]">
                      <div className="border-r border-[#E0DFD9] py-4 pr-4">
                        <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Modules</p>
                        <p className="mt-2 text-[13px] font-medium">{selectedModules.size} selected</p>
                      </div>
                      <div className="py-4 pl-4">
                        <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Monthly</p>
                        <p className="mt-2 text-[13px] font-medium">KSh {formatNumber(moduleTotal(selectedModules))}</p>
                      </div>
                    </div>
                    <p className="mt-4 text-[11px] leading-5 text-slate-500">Module prices in this prototype are placeholders. Production values should come from your billing configuration.</p>
                    <button
                      onClick={() => essentialsComplete && openDrawer("review")}
                      disabled={!essentialsComplete}
                      className={`ink-button mt-5 w-full px-4 py-3 text-[12px] font-medium ${
                        essentialsComplete
                          ? "border border-[#101828] bg-[#101828] text-white hover:bg-[#2348C7] hover:border-[#2348C7]"
                          : "cursor-not-allowed border border-[#CBD5E1] bg-[#F3F6FA] text-slate-400"
                      }`}
                    >
                      {essentialsComplete ? "Review & create workspace →" : "Complete required records"}
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          </main>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
         PROVISIONING VIEW
         ═══════════════════════════════════════════════════════════════════ */}
      {view === "provisioning" && (
        <section className="min-h-screen bg-[#F3F6FA] animate-fade-up">
          <header className="border-b border-[#DCE3EA] bg-white">
            <div className="mx-auto flex h-16 max-w-5xl items-center px-5 sm:px-8">
              <div className="wordmark text-slate-950">SekaniPay</div>
            </div>
          </header>
          <main className="mx-auto max-w-xl px-5 py-14 sm:px-8">
            <div className="text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-sm bg-sekani-50">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#D8E8FF] border-t-[#2F74D8]" />
              </div>
              <h1 className="mt-6 text-3xl font-bold tracking-tight">Setting up {business.company}</h1>
              <p className="mt-3 leading-7 text-slate-500">We are creating your tenant, first company, modules and administrator access.</p>
            </div>
            <div className="mt-9 square-card border border-slate-200 bg-white p-5">
              {PROVISION_TASKS.map((task, i) => (
                <div key={i} className="flex items-center gap-3 border-b border-slate-100 py-3.5 last:border-0">
                  <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                    provisionComplete[i]
                      ? "bg-[#0F7B4D] text-white text-xs"
                      : provisionStep === i
                        ? ""
                        : "border border-slate-200"
                  }`}>
                    {provisionComplete[i] ? "✓" : provisionStep === i ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#D8E8FF] border-t-[#2F74D8]" />
                    ) : null}
                  </div>
                  <span className={`text-sm ${
                    provisionComplete[i] ? "text-slate-700" : provisionStep === i ? "font-medium text-slate-800" : "text-slate-400"
                  }`}>{task}</span>
                </div>
              ))}
            </div>
          </main>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
         SUCCESS VIEW
         ═══════════════════════════════════════════════════════════════════ */}
      {view === "success" && (
        <section className="min-h-screen bg-[#F3F6FA] animate-fade-up">
          <header className="border-b border-[#DCE3EA] bg-white">
            <div className="mx-auto flex h-16 max-w-5xl items-center px-5 sm:px-8">
              <div className="wordmark text-slate-950">SekaniPay</div>
            </div>
          </header>
          <main className="mx-auto max-w-xl px-5 py-14 text-center sm:px-8">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#ECFDF5] text-2xl font-bold text-[#0F7B4D]">✓</div>
            <span className="mono mt-6 inline-flex bg-[#ECFDF5] px-3 py-1 text-[10px] uppercase tracking-[.1em] font-semibold text-[#0F7B4D]">Workspace ready</span>
            <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Your SekaniPay workspace is ready</h1>
            <p className="mx-auto mt-3 max-w-lg leading-7 text-slate-500">Your workspace owner account has been attached to the tenant. We have also prepared the workspace URL and any team invitations you added.</p>
            <div className="mt-8 square-card border border-slate-200 bg-white p-6 text-left">
              <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Workspace</p>
              <p className="mt-2 break-all text-lg font-semibold text-sekani-700">https://{workspace.subdomain}.sekanipay.com</p>
              <div className="mt-5 border-t border-slate-100 pt-5">
                <p className="text-sm font-semibold">Confirmation email sent</p>
                <p className="mt-1 text-sm leading-6 text-slate-500">Workspace details and activation information were sent to {account.email}.</p>
              </div>
            </div>
            <button onClick={openTenant} className="ink-button mt-7 inline-flex bg-[#0F1B2D] px-6 py-3.5 text-sm font-semibold text-white hover:bg-slate-800">
              Open SekaniPay →
            </button>
          </main>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
         DRAWER OVERLAY
         ═══════════════════════════════════════════════════════════════════ */}
      {drawer && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/25 backdrop-blur-[1px] transition-opacity"
          onClick={(e) => { if (e.target === e.currentTarget) setDrawer(null); }}
        >
          <aside className="absolute bottom-0 right-0 top-0 flex w-full max-w-2xl flex-col bg-white shadow-lg animate-slide-in-right">
            {/* ── Business details drawer ── */}
            {drawer === "business" && (
              <>
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 sm:px-8">
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[.13em] text-sekani-700">Business details</p>
                    <h2 className="mt-1 text-xl font-bold tracking-tight">Tell us about your organization</h2>
                  </div>
                  <button onClick={() => setDrawer(null)} className="flex h-10 w-10 items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-7 sm:px-8">
                  <div className="max-w-lg">
                    <p className="mb-7 text-sm leading-6 text-slate-500">Enter the primary company details for your workspace. You can add more companies later.</p>
                    <div className="space-y-5">
                      <div>
                        <label className="mb-2 block text-sm font-medium">Primary company</label>
                        <input value={draftBusiness.company} onChange={e => setDraftBusiness(p => ({ ...p, company: e.target.value }))} placeholder="Sekanipay Kenya Ltd" className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none" />
                        <p className="mt-2 text-xs leading-5 text-slate-400">You can create more companies, branches or legal entities after setup.</p>
                      </div>
                      <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-medium">Country</label>
                          <select value={draftBusiness.country} onChange={e => setDraftBusiness(p => ({ ...p, country: e.target.value }))} className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none">
                            <option value="">Select option</option>
                            {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium">Industry</label>
                          <select value={draftBusiness.industry} onChange={e => setDraftBusiness(p => ({ ...p, industry: e.target.value }))} className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none">
                            <option value="">Select option</option>
                            {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium">Number of employees</label>
                        <select value={draftBusiness.size} onChange={e => setDraftBusiness(p => ({ ...p, size: e.target.value }))} className="w-full border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none">
                          <option value="">Select option</option>
                          {SIZES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="square-card border border-sekani-100 bg-sekani-50 p-4">
                        <p className="text-sm font-semibold text-sekani-800">Multi-company ready</p>
                        <p className="mt-1 text-xs leading-5 text-sekani-800">The subdomain belongs to the organization, while companies live inside the workspace.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-slate-200 bg-white px-6 py-4 sm:px-8">
                  <div className="flex justify-end gap-3">
                    <button onClick={() => setDrawer(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 square-card">Cancel</button>
                    <button onClick={saveBusiness} className="square-card bg-[#0F1B2D] px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Save &amp; return</button>
                  </div>
                </div>
              </>
            )}

            {/* ── Workspace address drawer ── */}
            {drawer === "workspace" && (
              <>
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 sm:px-8">
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[.13em] text-sekani-700">Workspace identity</p>
                    <h2 className="mt-1 text-xl font-bold tracking-tight">Choose your SekaniPay address</h2>
                  </div>
                  <button onClick={() => setDrawer(null)} className="flex h-10 w-10 items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-7 sm:px-8">
                  <div className="max-w-lg">
                    <p className="mb-7 text-sm leading-6 text-slate-500">This is the subdomain employees and administrators will use to access the organization workspace.</p>
                    <label className="mb-2 block text-sm font-medium">Workspace subdomain</label>
                    <div className="flex overflow-hidden border border-slate-200 square-card focus-within:border-sekani-400 focus-within:ring-2 focus-within:ring-sekani-100">
                      <input
                        value={draftSubdomain}
                        onChange={e => handleSubdomainInput(e.target.value)}
                        placeholder="sekanipay"
                        className="min-w-0 flex-1 px-4 py-3.5 text-sm outline-none"
                      />
                      <span className="flex items-center border-l border-slate-200 bg-slate-50 px-4 text-sm text-slate-500">.sekanipay.com</span>
                    </div>
                    <div className="mt-3 min-h-[24px] text-sm">
                      {workspace.status === "checking" && <span className="flex items-center gap-2 text-slate-500"><span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#D8E8FF] border-t-[#2F74D8]" />Checking availability…</span>}
                      {workspace.status === "available" && <span className="font-medium text-[#0F7B4D]">✓ {workspace.subdomain}.sekanipay.com is available</span>}
                      {workspace.status === "unavailable" && <span className="font-medium text-rose-500">That workspace address is already taken.</span>}
                      {workspace.status === "invalid" && <span className="text-rose-500">Use 3-32 lowercase letters, numbers or hyphens.</span>}
                      {workspace.status === "idle" && <span className="text-slate-400">Start typing and availability will be checked automatically.</span>}
                    </div>

                    {/* Browser preview */}
                    <div className="mt-8 overflow-hidden square-card border border-slate-200 bg-slate-50">
                      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
                        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
                        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
                        <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
                        <div className="mono ml-3 flex-1 truncate bg-slate-100 px-3 py-1.5 text-xs text-slate-500 square-card">
                          https://{workspace.subdomain || "yourcompany"}.sekanipay.com
                        </div>
                      </div>
                      <div className="px-6 py-9 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center bg-[#0F1B2D] text-white square-card">S</div>
                        <p className="mt-4 text-lg font-semibold">Welcome to SekaniPay</p>
                        <p className="mt-1 text-xs text-slate-400">Sign in to your organization workspace</p>
                        <div className="mx-auto mt-6 max-w-xs border border-slate-200 bg-white p-4 text-left square-card">
                          <p className="text-xs text-slate-400">Work email</p>
                          <div className="mt-2 h-10 bg-slate-100 square-card" />
                          <div className="mt-3 h-10 bg-[#0F1B2D] square-card" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-slate-200 bg-white px-6 py-4 sm:px-8">
                  <div className="flex justify-end gap-3">
                    <button onClick={() => setDrawer(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 square-card">Cancel</button>
                    <button onClick={saveWorkspace} className="square-card bg-[#0F1B2D] px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Save &amp; return</button>
                  </div>
                </div>
              </>
            )}

            {/* ── Modules drawer ── */}
            {drawer === "modules" && (
              <>
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 sm:px-8">
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[.13em] text-sekani-700">Workspace modules</p>
                    <h2 className="mt-1 text-xl font-bold tracking-tight">Choose your SekaniPay modules</h2>
                  </div>
                  <button onClick={() => setDrawer(null)} className="flex h-10 w-10 items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-7 sm:px-8">
                  <p className="mb-7 max-w-xl text-sm leading-6 text-slate-500">Choose what the organization needs now. Optional modules can be changed later from Settings → Modules.</p>
                  <div className="space-y-8">
                    {MODULE_GROUPS.map(group => {
                      const mods = MODULES.filter(m => m.group === group.id);
                      return (
                        <section key={group.id}>
                          <div className="mb-4">
                            <h3 className="text-sm font-semibold">{group.title}</h3>
                            <p className="mt-1 text-xs text-slate-400">{group.desc}</p>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2">
                            {mods.map(mod => {
                              const selected = selectedModules.has(mod.id);
                              const deps = selectedDependents(mod.id, selectedModules);
                              return (
                                <button
                                  key={mod.id}
                                  type="button"
                                  onClick={() => toggleModule(mod.id)}
                                  className={`square-card border p-4 text-left transition-colors ${
                                    selected ? "border-[#79AEFF] bg-sekani-50" : "border-slate-200 bg-white hover:border-slate-300"
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className={`flex h-9 w-9 items-center justify-center square-card text-sm ${
                                      selected ? "bg-sekani-100 text-sekani-700" : "bg-slate-100 text-slate-500"
                                    }`}>{selected ? "✓" : "+"}</div>
                                    {mod.required
                                      ? <span className="mono bg-sekani-50 px-2 py-1 text-[10px] font-semibold text-sekani-700">INCLUDED</span>
                                      : <span className="text-sm font-semibold">KSh {formatNumber(mod.price)}<span className="font-normal text-slate-400">/mo</span></span>
                                    }
                                  </div>
                                  <p className="mt-4 text-sm font-semibold">{mod.name}</p>
                                  <p className="mt-1 text-xs leading-5 text-slate-500">{mod.desc}</p>
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>

                  {/* Sticky cost summary */}
                  <div className="sticky bottom-0 mt-8 square-card border border-slate-200 bg-white/95 p-5 shadow-md backdrop-blur">
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-xs text-slate-400">Estimated monthly</p>
                        <p className="mt-1 text-sm text-slate-500">{selectedModules.size} modules selected</p>
                      </div>
                      <p className="text-2xl font-bold">KSh {formatNumber(moduleTotal(selectedModules))}<span className="text-sm font-medium text-slate-400"> /mo</span></p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-slate-200 bg-white px-6 py-4 sm:px-8">
                  <div className="flex justify-end gap-3">
                    <button onClick={() => setDrawer(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 square-card">Cancel</button>
                    <button onClick={saveModules} className="square-card bg-[#0F1B2D] px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Save selection</button>
                  </div>
                </div>
              </>
            )}

            {/* ── Invites drawer ── */}
            {drawer === "invites" && (
              <>
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 sm:px-8">
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[.13em] text-sekani-700">Optional</p>
                    <h2 className="mt-1 text-xl font-bold tracking-tight">Invite your HR team</h2>
                  </div>
                  <button onClick={() => setDrawer(null)} className="flex h-10 w-10 items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-7 sm:px-8">
                  <div className="max-w-xl">
                    <p className="mb-7 text-sm leading-6 text-slate-500">Invitations are prepared now and sent after the workspace is successfully created.</p>
                    <div className="space-y-3">
                      {invites.map((invite, i) => (
                        <div key={i} className="grid gap-3 square-card border border-slate-200 p-4 sm:grid-cols-[1fr_160px_40px]">
                          <input
                            type="email"
                            value={invite.email}
                            onChange={e => setInvites(prev => prev.map((inv, j) => j === i ? { ...inv, email: e.target.value } : inv))}
                            placeholder="colleague@company.com"
                            className="border border-slate-200 bg-white px-4 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none"
                          />
                          <select
                            value={invite.role}
                            onChange={e => setInvites(prev => prev.map((inv, j) => j === i ? { ...inv, role: e.target.value } : inv))}
                            className="border border-slate-200 bg-white px-3 py-3 text-sm square-card focus:border-sekani-400 focus:ring-2 focus:ring-sekani-100 focus:outline-none"
                          >
                            {["Administrator", "HR Admin", "HR Manager"].map(r => <option key={r}>{r}</option>)}
                          </select>
                          <button
                            onClick={() => setInvites(prev => { const next = prev.filter((_, j) => j !== i); return next.length ? next : [{ email: "", role: "HR Admin" }]; })}
                            className="flex h-10 w-10 items-center justify-center self-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 square-card"
                          >✕</button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setInvites(prev => [...prev, { email: "", role: "HR Admin" }])}
                      className="mt-4 border border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-600 hover:border-sekani-300 hover:bg-sekani-50 square-card"
                    >+ Add another person</button>
                    <div className="mt-8 square-card bg-slate-50 p-4">
                      <p className="text-sm font-medium">Secure invitations</p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">Invitees should receive activation links rather than plaintext passwords.</p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-slate-200 bg-white px-6 py-4 sm:px-8">
                  <div className="flex justify-end gap-3">
                    <button onClick={() => setDrawer(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 square-card">Skip for now</button>
                    <button onClick={saveInvites} className="square-card bg-[#0F1B2D] px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Save invitations</button>
                  </div>
                </div>
              </>
            )}

            {/* ── Review drawer ── */}
            {drawer === "review" && (
              <>
                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 sm:px-8">
                  <div>
                    <p className="mono text-[10px] uppercase tracking-[.13em] text-sekani-700">Ready to launch</p>
                    <h2 className="mt-1 text-xl font-bold tracking-tight">Review your SekaniPay workspace</h2>
                  </div>
                  <button onClick={() => setDrawer(null)} className="flex h-10 w-10 items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-7 sm:px-8">
                  <div className="space-y-4">
                    {/* Organization */}
                    <div className="square-card border border-slate-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Company</p>
                          <p className="mt-2 break-words text-sm font-semibold">{business.company}</p>
                          <p className="mt-1 break-words text-xs leading-5 text-slate-500">{business.country}</p>
                        </div>
                        <button onClick={() => openDrawer("business")} className="shrink-0 text-xs font-semibold text-sekani-700">Edit</button>
                      </div>
                    </div>

                    {/* Workspace URL */}
                    <div className="square-card border border-slate-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Workspace URL</p>
                          <p className="mt-2 break-words text-sm font-semibold">{workspace.subdomain}.sekanipay.com</p>
                          <p className="mt-1 break-words text-xs leading-5 text-slate-500">Organization tenant address</p>
                        </div>
                        <button onClick={() => openDrawer("workspace")} className="shrink-0 text-xs font-semibold text-sekani-700">Edit</button>
                      </div>
                    </div>

                    {/* Modules */}
                    <div className="square-card border border-slate-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Modules</p>
                          <p className="mt-2 break-words text-sm font-semibold">
                            {Array.from(selectedModules).map(id => getModule(id)?.name).join(", ")}
                          </p>
                          <p className="mt-1 break-words text-xs leading-5 text-slate-500">KSh {formatNumber(moduleTotal(selectedModules))}/month estimated</p>
                        </div>
                        <button onClick={() => openDrawer("modules")} className="shrink-0 text-xs font-semibold text-sekani-700">Edit</button>
                      </div>
                    </div>

                    {/* Invitations */}
                    <div className="square-card border border-slate-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="mono text-[9px] uppercase tracking-[.1em] text-slate-400">Team invitations</p>
                          <p className="mt-2 break-words text-sm font-semibold">
                            {validInvites(invites).length > 0
                              ? `${validInvites(invites).length} administrator${validInvites(invites).length === 1 ? "" : "s"}`
                              : "No invitations"}
                          </p>
                          <p className="mt-1 break-words text-xs leading-5 text-slate-500">
                            {validInvites(invites).length > 0
                              ? validInvites(invites).map(i => i.email).join(", ")
                              : "Invite people later from the workspace"}
                          </p>
                        </div>
                        <button onClick={() => openDrawer("invites")} className="shrink-0 text-xs font-semibold text-sekani-700">Edit</button>
                      </div>
                    </div>

                    {/* Ready notice */}
                    <div className="square-card border border-sekani-100 bg-sekani-50 p-5">
                      <p className="text-sm font-semibold text-sekani-800">Everything essential is ready</p>
                      <p className="mt-1 text-xs leading-5 text-sekani-800">SekaniPay will provision the organization, first company, subdomain and selected modules.</p>
                    </div>

                    {/* Confirmation */}
                    <label className="flex cursor-pointer items-start gap-3">
                      <input type="checkbox" checked={launchConfirmed} onChange={e => setLaunchConfirmed(e.target.checked)} className="mt-1 h-4 w-4 border-slate-300 accent-sekani-700" />
                      <span className="text-sm leading-6 text-slate-500">I confirm the setup above is correct and I am ready to create this workspace.</span>
                    </label>
                  </div>
                </div>
                <div className="border-t border-slate-200 bg-white px-6 py-4 sm:px-8">
                  <div className="flex justify-end gap-3">
                    <button onClick={() => setDrawer(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 square-card">Back</button>
                    <button onClick={createWorkspace} disabled={loading} className="square-card bg-[#0F1B2D] px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                      {loading ? "Creating…" : "Create workspace →"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </aside>
        </div>
      )}

      {/* ── Drawer slide-in animation ── */}
      <style jsx>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s cubic-bezier(0.22, 0.8, 0.28, 1) both;
        }
      `}</style>
    </div>
  );
}
